import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { ArrowLeft, ArrowRight, Check, Save, Sparkles } from 'lucide-react'
import { useGrades, useGradeSubjects, useSchoolAddGrade, useUserInfo, useNationalExams, useExamDetail, useChangeExamStatus } from '../../react-query/queries'
import { useAuth } from '../../utils/Auth/AuthProvider'
import { SchoolApi } from '../../school_panel/api/client'
import { useAssignableGroups, useSectionsList } from '../../school_panel/api/hooks'
import { Badge, Button, PageHeader, PanelCard, Segmented, Spinner, Switch } from '../../school_panel/components/ui'
import { fieldClass, NAME_MAX } from '../../school_panel/lib/fieldClass'
import ExamProgressStepper from './ExamProgressStepper'
import ExamQuestionAddCard from './ExamQuestionAddCard'
import ExamProcessingPipeline from './ExamProcessingPipeline'
import ExamReviewStep from './ExamReviewStep'
import { hasExplanation } from './ExamQuestionsInitial'
import { isFailedResponse, partialSave, savedAgo, toReviewQuestions, type ReviewQuestion } from './examReview'
import { ExamSessionProvider } from '../../store/ExamSessionStore'
import { useSchoolExamQuestionAdd } from '../../react-query/queries'
import type { ExamKind } from '../../school_panel/api/types'
import AssignFields, { EMPTY_ASSIGN, assignError, assignPayload, audienceText, type AssignValue } from './AssignFields'
import { LINK_BTN } from '../../school_panel/components/ui/linkButton'
import { useTaughtSubjects } from '../../school_panel/lib/teaching'
import UniCoursePicker, { EMPTY_UNI_PICK, uniCoursesText, uniOwnerName, uniPickError, type UniPick } from './UniCoursePicker'

const WIZ_LABEL = 'mb-1.5 block text-[13px] font-semibold text-ink'
const BACK_ICON = <ArrowLeft size={18} strokeWidth={1.8} aria-hidden />
const NEXT_ICON = <ArrowRight size={18} strokeWidth={1.8} aria-hidden />
const NO_COURSES: string[] = []

const gradeLabelOf = (grade: { grade_name: string }) => {
    const name = String(grade.grade_name ?? '').replace(/^\s*grade\s+/i, '')
    return /^\d+$/.test(name) ? `Grade ${name}` : String(grade.grade_name ?? '')
}

/** Header status: "Draft · saved 2 min ago" once the exam exists. */
const DraftPill = ({ examId }: { examId: string }) => {
    const { token } = useAuth()
    const { data } = useExamDetail(token, examId)
    const exam = (data as any)?.exam
    if (!exam) return null
    const active = String(exam.status ?? 'Draft') === 'Active'
    const ago = savedAgo(exam.updated)
    return <Badge tone={active ? 'success' : 'info'}>{[active ? 'Active' : 'Draft', ago].filter(Boolean).join(' · ')}</Badge>
}

/** "Grade 10 Mathematics · 25 questions · 60 minutes" for an existing exam. */
const ExamSubline = ({ examId, questionCount }: { examId: string; questionCount?: number }) => {
    const { token } = useAuth()
    const { data } = useExamDetail(token, examId)
    const exam = (data as any)?.exam
    if (!exam) return <>Loading exam details…</>
    const grade = String(exam.school_grade_name || exam.grade || '')
    const time = exam.time ?? exam.time_given
    return <>{[
        [grade ? gradeLabelOf({ grade_name: grade }) : '', exam.subject || ''].filter(Boolean).join(' '),
        questionCount != null ? `${questionCount} question${questionCount === 1 ? '' : 's'}` : '',
        time ? `${time} minutes` : '',
    ].filter(Boolean).join(' · ')}</>
}

/** Page header shared by the add-exam wizard and the direct add-questions route. */
export const ExamAddHeader = ({ subtitle, examId, questionCount }: { subtitle?: React.ReactNode; examId?: string | null; questionCount?: number }) => (
    <PageHeader
        eyebrow="Exams · Add exam"
        title="Create an exam"
        subtitle={examId ? <ExamSubline examId={examId} questionCount={questionCount} /> : subtitle}
        actions={
            <>
                {examId && <DraftPill examId={examId} />}
                <Link to="/exams/exam-list" className={LINK_BTN}><ArrowLeft size={18} strokeWidth={1.8} aria-hidden />Exam list</Link>
                <Link to="/exams/generate" className={LINK_BTN}><Sparkles size={18} strokeWidth={1.8} aria-hidden />Generate with AI instead</Link>
            </>
        }
    />
)

/** Design `.foot`: step note on the left, navigation on the right; never floats over content. */
export const WizardFooter = ({ note, children }: { note: React.ReactNode; children?: React.ReactNode }) => (
    <div className="mt-[18px] flex flex-wrap items-center justify-between gap-3 rounded-[18px] border border-line bg-surface px-[18px] py-3.5 shadow-foot">
        <span className="min-w-0 text-[13px] text-muted">{note}</span>
        <div className="flex flex-wrap items-center gap-2.5">{children}</div>
    </div>
)

/** A label + control pair in the artboard style; the error replaces the hint. */
const WizField = ({ label, htmlFor, required, hint, error, optional, children }: {
    label: string; htmlFor: string; required?: boolean; hint?: string; error?: string; optional?: boolean; children: React.ReactNode
}) => (
    <div className="min-w-0">
        <label htmlFor={htmlFor} className={WIZ_LABEL}>
            {label}
            {required && <span aria-hidden className="ml-0.5 text-danger">*</span>}
            {optional && <span className="font-normal text-muted"> (optional)</span>}
        </label>
        {children}
        {error ? <p className="mt-1.5 text-xs text-danger">{error}</p> : hint ? <p className="mt-1.5 text-xs text-muted">{hint}</p> : null}
    </div>
)

/** Publishes the exam with the same status call the exam page's Publishing switch uses. */
const PublishExamButton = ({ examId, onPublished }: { examId: string; onPublished: () => void }) => {
    const { token } = useAuth()
    const { mutateAsync, isPending } = useChangeExamStatus(examId)
    const [error, setError] = useState<string | null>(null)
    return (
        <>
            {error && <span role="alert" className="text-[13px] text-danger-ink">{error}</span>}
            <Button
                loading={isPending}
                icon={<Check size={18} strokeWidth={1.8} aria-hidden />}
                onClick={async () => {
                    setError(null)
                    try {
                        // changeExamStatus resolves with the error instead of throwing, so check the reply.
                        const res: any = await mutateAsync({ toBeValidated: token, exam_id: examId, status: 'Active' })
                        if (isFailedResponse(res) || res?.status !== 'Active') throw new Error('publish failed')
                        onPublished()
                    } catch {
                        setError('The exam could not be published. Try again.')
                    }
                }}
            >
                Publish exam
            </Button>
        </>
    )
}

const ExamAddWizardInner: React.FC = () => {
    const { token } = useAuth()

    // 1 details, 2 questions, 3 processing, 4 review & publish
    const [step, setStep] = useState<number>(1)
    // The Worksheets / Homework pages open the wizard with ?kind= preset; a saved group's
    // Assign menu (Class performance > Grouping) adds ?grade=&subject=&group=.
    const [searchParams] = useSearchParams()
    const queryId = (key: string) => { const v = Number(searchParams.get(key)); return Number.isInteger(v) && v > 0 ? String(v) : null }
    const queryGrade = queryId('grade')
    const querySubject = queryGrade ? queryId('subject') : null
    const queryGroup = querySubject ? Number(queryId('group')) || null : null
    const [gradeStateId, setGradeStateId] = useState<string>(() => { if (queryGrade) return queryGrade; try { return localStorage.getItem('add_exam_grade') || '-1' } catch { return '-1' } })
    const [subjectStateId, setSubjectStateId] = useState<string>(() => { if (queryGrade) return querySubject ?? '-1'; try { return localStorage.getItem('add_exam_subject') || '-1' } catch { return '-1' } })
    const [kind, setKind] = useState<ExamKind>(() => {
        const k = searchParams.get('kind')
        return k === 'worksheet' || k === 'homework' ? k : 'exam'
    })
    const isExam = kind === 'exam'
    const kindLabel = kind === 'exam' ? 'exam' : kind
    const [isAdding, setISAdding] = useState(false)
    const [submitError, setSubmitError] = useState<string | null>(null)

    const [createdExamId, setCreatedExamId] = useState<string | null>(null)
    const [pendingQuestions, setPendingQuestions] = useState<any[] | null>(null)
    const [questionList, setQuestionList] = useState<any[]>([])
    const [review, setReview] = useState<ReviewQuestion[] | null>(null)
    const [partial, setPartial] = useState<{ saved: number; sent: number; errors: string[] } | null>(null)
    const [questionsExtracted, setQuestionsExtracted] = useState<boolean>(false) // New state to track if questions are extracted
    const [isExtractingQuestions, setIsExtractingQuestions] = useState<boolean>(false) // New state for loading during extraction
    const [extractQuestionsFn, setExtractQuestionsFn] = useState<(() => void) | null>(null) // New state to hold the extract function from child
    const [proceedToMetaFn, setProceedToMetaFn] = useState<(() => void) | null>(null)
    const { mutateAsync: schoolExamQuestionAdd } = useSchoolExamQuestionAdd()
    const navigate = useNavigate()

    const { status: gradeStatus, data: gradeData } = useGrades(token)
    const { status: subjectStatus, data: subjectData } = useGradeSubjects(token, gradeStateId)
    // New work: a teacher only for the grades and subjects they teach (homeroom is not enough).
    const taught = useTaughtSubjects()
    const gradeTiles = (gradeData?.grades ?? []).filter((g: any) => taught.grades == null || taught.grades.includes(g.id))
    const taughtHere = taught.forGrade(Number(gradeStateId))
    const subjectTiles = (subjectData?.subjects ?? []).filter((s: any) => taughtHere == null || taughtHere.includes(s.id))
    const { mutate: schoolAddExam } = useSchoolAddGrade()
    const gradesAnalytics = useQuery({ queryKey: ['analytics.grades'], queryFn: () => SchoolApi.analyticsGrades(), retry: false })
    const gradeStudentCount = (id: number) => (gradesAnalytics.data?.grades ?? []).find((g) => g.id === id)?.student_count

    const memoizedOnRegisterExtractFn = useCallback((fn: () => void) => setExtractQuestionsFn(() => fn), [setExtractQuestionsFn]);
    const memoizedOnRegisterProceedFn = useCallback((fn: () => void) => setProceedToMetaFn(() => fn), [setProceedToMetaFn]);
    const memoizedOnProceedToMeta = useCallback((qs: any[]) => { setPendingQuestions(qs); setQuestionsExtracted(true); }, [setPendingQuestions, setQuestionsExtracted]);
    const memoizedOnQuestionsExtracted = useCallback((qs?: any[]) => {
        setQuestionsExtracted(true);
        if (qs && qs.length > 0) setPendingQuestions(qs);
    }, [setQuestionsExtracted, setPendingQuestions]);

    const [formdata, setFormdata] = useState({ name: '', given_time: 60, description: '' })
    const [isNational, setIsNational] = useState<boolean>(false)
    const [selectedNationalExam, setSelectedNationalExam] = useState<string>('-1')
    const [assign, setAssign] = useState<AssignValue>(() => queryGroup ? { ...EMPTY_ASSIGN, mode: 'students' } : EMPTY_ASSIGN)
    // ?group=: start with that saved group's students once its members are known.
    const groupsQuery = useAssignableGroups(queryGroup ? Number(queryGrade) : null, queryGroup ? Number(querySubject) : null)
    const groupApplied = useRef(false)
    useEffect(() => {
        if (groupApplied.current || !queryGroup || !groupsQuery.data) return
        groupApplied.current = true
        const g = groupsQuery.data.groups.find((x) => x.id === queryGroup)
        if (g) setAssign((a) => ({ ...a, mode: 'students', studentIds: [...g.member_ids] }))
    }, [groupsQuery.data, queryGroup])
    const [serverErrors, setServerErrors] = useState<Record<string, string>>({})

    const { role } = useAuth()

    // fetch user info (which may include institution_type)
    const { data: userData } = useUserInfo()
    // YADA Education Hub admins also write exams for university/college students.
    const isHubAdmin = !!userData?.is_hub_admin
    const [level, setLevel] = useState<'school' | 'uni'>(() => searchParams.get('level') === 'uni' ? 'uni' : 'school')
    const uniMode = isHubAdmin && level === 'uni'
    const [uni, setUni] = useState<UniPick>(EMPTY_UNI_PICK)
    const uniCourseIds = useMemo(() => uni.courses.map((c) => String(c.id)), [uni.courses])

    // prefer institution_type from user info; fallback to role-derived mapping
    const targetLevel: 'school' | 'college_university' | undefined = ((): any => {
        const raw = (userData?.institution_type ?? userData?.institution_name ?? '') as string
        const instType = String(raw || '').trim().toLowerCase()
        if (instType.includes('school')) return 'school'
        if (instType.includes('college') || instType.includes('university') || instType.includes('college_university')) return 'college_university'
        // fallback to role
        const r = String(role ?? '').toLowerCase()
        if (r === 'school') return 'school'
        if (r.includes('college') || r.includes('university') || r === 'college_university') return 'college_university'
        return undefined
    })()

    // always enable the national exams fetch; targetLevel may be undefined which returns all
    const { status: nationalStatus, data: nationalData } = useNationalExams(targetLevel, true)

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = event.target
        setFormdata((prev) => ({ ...prev, [name]: name === 'given_time' ? Number(value) : value }))
    }

    const nameError = formdata.name.trim().length >= 3 ? '' : `Enter ${isExam ? 'an exam' : `a ${kind}`} name (at least 3 characters).`
    const timeError = !isExam || (Number(formdata.given_time) > 0 && Number(formdata.given_time) <= 300) ? '' : 'Time must be between 1 and 300 minutes.'
    const [showStep1Errors, setShowStep1Errors] = useState(false)
    const gradeError = uniMode ? '' : gradeStateId === '-1' ? 'Select a grade to continue.' : ''
    // A remembered grade/subject the teacher does not teach counts as no choice.
    const subjectError = uniMode ? uniPickError(uni) : subjectStateId === '-1' || (taughtHere != null && !taughtHere.includes(Number(subjectStateId)))
        || (taught.grades != null && !taught.grades.includes(Number(gradeStateId)))
        ? 'Select a subject to continue.' : ''
    const isValidDetails = () => !nameError && !timeError && (uniMode || !assignError(assign))
    const created = createdExamId != null

    const createExam = async (e?: React.FormEvent) => {
        if (e) e.preventDefault()
        if (isAdding) return
        setSubmitError(null)
        // The exam is already saved as a draft: coming back to Details only moves on.
        if (created) { setStep(2); return }
        setShowStep1Errors(true)
        if (gradeError || subjectError) { setSubmitError(gradeError || subjectError); return }
        if (!isValidDetails()) { setSubmitError(nameError || timeError || assignError(assign)); return }
        if (uniMode) {
            // A department's exam: one course, or several for a mixed exam. The Hub owns it.
            const uniData = new FormData()
            uniData.append('name', formdata.name.trim())
            uniData.append('kind', 'exam')
            uniData.append('time', `${formdata.given_time}`)
            uniData.append('desc', formdata.description?.trim() || '')
            uniData.append('courses', JSON.stringify(uni.courses.map((c) => c.id)))
            uniData.append('field_of_study', String(uni.fieldId))
            if (uni.universityId != null) uniData.append('university', String(uni.universityId))
            uniData.append('exam_scope', 'none')
            setServerErrors({})
            setISAdding(true)
            schoolAddExam({ toBeValidated: token, data: uniData } as any, {
                onSuccess: (data: any) => {
                    setISAdding(false)
                    if (data && data['done'] === 'done' && data['exam_id']) { setCreatedExamId(String(data['exam_id'])); setStep(2) }
                    else setSubmitError('The exam could not be created. Try again.')
                },
                onError: (err: any) => {
                    setISAdding(false)
                    const msg = err?.response?.data?.courses || err?.response?.data?.detail
                    setSubmitError(typeof msg === 'string' ? msg : 'The exam could not be created. Try again.')
                },
            } as any)
            return
        }
        // when national exam is toggled on, require selection of a national exam
        if (isNational) {
            if (!selectedNationalExam || selectedNationalExam === '-1') {
                setSubmitError('Choose a national exam, or turn off the national exam option.')
                return
            }
        }
        setServerErrors({})
        setISAdding(true)
        const postData = new FormData()
        postData.append('name', formdata.name.trim())
        postData.append('kind', kind)
        if (isExam) postData.append('time', `${formdata.given_time}`)
        postData.append('desc', formdata.description?.trim() || '')
        postData.append('grade_id', gradeStateId)
        postData.append('subject_id', subjectStateId)
        // optional: exam scope and national exam id
        if (isNational) {
            postData.append('exam_scope', 'national')
            if (selectedNationalExam && selectedNationalExam !== '-1') postData.append('national_exam', selectedNationalExam)
        } else {
            postData.append('exam_scope', 'none')
        }
        // Phase 2-D additions (spec §5.1)
        const p = assignPayload(assign)
        postData.append('assignment_scope', p.assignment_scope)
        p.assigned_student_ids.forEach((id) => postData.append('assigned_student_ids', String(id)))
        p.assigned_section_ids.forEach((id) => postData.append('assigned_section_ids', String(id)))
        postData.append('instructions', p.instructions)
        postData.append('results_release', p.results_release)
        if (p.due_at) postData.append('due_at', p.due_at)

        schoolAddExam({ toBeValidated: token, data: postData } as any, {
            onSuccess: (data: any) => {
                setISAdding(false)
                if (data && data['done'] === 'done') {
                    try { localStorage.setItem('add_exam_grade', gradeStateId); localStorage.setItem('add_exam_subject', subjectStateId) } catch {}
                    // instead of navigating, keep the wizard on the same URL and move to the question step
                    const newId = String(data['exam_id'])
                    if (kind !== 'exam') {
                        // worksheets and homework are authored in their own editor
                        navigate(`/worksheets/${newId}/edit`)
                        return
                    }
                    setCreatedExamId(newId)
                    setStep(2)
                } else {
                    setSubmitError('The exam could not be created. Try again.')
                }
            },
            onError: (err: any) => {
                setISAdding(false)
                const body = err?.response?.data
                const fieldErrs: Record<string, string> = {}
                for (const k of ['due_at', 'instructions', 'assigned_section_ids', 'assigned_student_ids']) {
                    const v = body?.[k]
                    if (v) fieldErrs[k] = Array.isArray(v) ? String(v[0]) : String(v)
                }
                if (Object.keys(fieldErrs).length) { setServerErrors(fieldErrs); setSubmitError(null); return }
                const msg = err?.response?.data?.detail || err?.message || 'The exam could not be created. Try again.'
                setSubmitError(typeof msg === 'string' ? msg : 'The exam could not be created. Try again.')
            }
        } as any)
    }

    // Design `.tile` / `.tile.on`.
    const tileClass = (selected: boolean) =>
        `flex min-h-[56px] min-w-0 flex-col justify-center gap-0.5 rounded-[14px] border-[1.5px] px-3 py-2.5 text-left text-sm font-semibold text-ink transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent disabled:cursor-default ${selected ? 'border-accent bg-accent-bg ring-[3px] ring-accent/20' : 'border-line-strong bg-surface hover:border-slate'}`

    const renderGradeTile = (grade: { id: number; grade_name: string }) => {
        const selected = String(grade.id) === gradeStateId
        const label = gradeLabelOf(grade)
        const n = gradeStudentCount(grade.id)
        return (
            <button key={grade.id} type="button" aria-pressed={selected} disabled={created} onClick={() => { setGradeStateId(String(grade.id)); setSubjectStateId('-1'); setAssign((a) => ({ ...a, sectionIds: [], studentIds: [] })) }} className={tileClass(selected)}>
                <span className="break-words">{label}</span>
                {typeof n === 'number' && <small className="text-xs font-medium text-muted">{n} students</small>}
            </button>
        )
    }

    const renderSubjectTile = (subject: { id: number; subject_name: string; question_count?: number | null }) => {
        const selected = String(subject.id) === subjectStateId
        return (
            <button key={subject.id} type="button" aria-pressed={selected} disabled={created} onClick={() => {
                // A group (or picked students) belongs to the subject it was listed for.
                if (!selected) setAssign((a) => (a.mode === 'students' ? { ...a, studentIds: [] } : a))
                setSubjectStateId(String(subject.id))
            }} className={tileClass(selected)}>
                <span className="break-words">{subject.subject_name}</span>
                {typeof subject.question_count === 'number' && <small className="text-xs font-medium text-muted">{subject.question_count} in bank</small>}
            </button>
        )
    }

    const handleSaveQuestions = useCallback(async (qs: any[], options?: any) => {
        const resp = await schoolExamQuestionAdd({ toBeValidated: token, data: qs as any, exam_id: String(createdExamId || '') })
        // addSchoolExamQuestions returns the error instead of throwing: surface it so the pipeline shows its save error.
        if (isFailedResponse(resp)) throw resp instanceof Error ? resp : new Error('The questions could not be saved.')
        if (!options?.partial && createdExamId) {
            // The questions are saved; the last step reviews them before publishing.
            // A 201 can still carry per-question errors. Do not throw: a retry would re-post (and duplicate) the saved ones.
            const part = partialSave(resp, qs.length)
            setPartial(part ? { ...part, sent: qs.length } : null)
            setReview(toReviewQuestions(qs, options?.chapters ?? []))
            setStep(4)
        }
        return resp
    }, [schoolExamQuestionAdd, token, createdExamId])

    const selectedGrade = gradeStatus === 'success' ? (gradeData?.grades ?? []).find((g: any) => String(g.id) === gradeStateId) : undefined
    const selectedSubject = subjectStatus === 'success' ? (subjectData?.subjects ?? []).find((s: any) => String(s.id) === subjectStateId) : undefined
    const gradeLabel = uniMode ? uni.fieldName : selectedGrade ? gradeLabelOf(selectedGrade) : ''
    const sectionsQuery = useSectionsList(Number(gradeStateId) > 0 ? Number(gradeStateId) : null)
    const who = uniMode ? `${uni.fieldName || 'the department'} students` : audienceText(assign, sectionsQuery.data?.sections ?? [], gradeLabel || undefined)
    const kindTitle = kind === 'exam' ? 'Exam' : kind === 'worksheet' ? 'Worksheet' : 'Homework'
    const blocker = gradeError || subjectError || nameError || timeError || (uniMode ? '' : assignError(assign) ?? '')
    const unexplained = questionList.filter((q) => !hasExplanation(q)).length
    const questionCount = review ? (partial ? partial.saved : review.length) : questionList.length
    const footNote =
        step === 1 ? `Step 1 of 3 · ${created ? 'details are saved as a draft' : (blocker && !isAdding ? blocker : 'your draft is created when you continue')}`
        : step === 2 ? `Step 2 of 3 · ${questionList.length} question${questionList.length === 1 ? '' : 's'} · ${unexplained} without explanation`
        : step === 3 ? 'Step 2 of 3 · Check chapters and explanations, then save the questions.'
        : `Step 3 of 3 · publishing makes the exam visible to ${who}`

    const detailsSubtitle = `What students will see on the ${kindLabel} cover.`
    const headerSubtitle = (() => {
        const parts = [
            uniMode ? [uni.fieldName, uniCoursesText(uni)].filter(Boolean).join(' · ')
                : [gradeLabel, selectedSubject?.subject_name ?? ''].filter(Boolean).join(' '),
            questionCount > 0 ? `${questionCount} question${questionCount === 1 ? '' : 's'}` : '',
            isExam && Number(formdata.given_time) > 0 && (gradeLabel || selectedSubject || uni.courses.length) ? `${formdata.given_time} minutes` : '',
        ].filter(Boolean)
        return parts.length ? parts.join(' · ') : `Create a new ${kindLabel}. ${isExam ? 'Questions are added after the details are saved.' : 'It opens in its own editor once the details are saved.'}`
    })()

    const stageSelectable = (first: number) => {
        if (step === 4) return first === 4
        if (first === 1) return true
        if (first === 2) return created
        return review != null
    }

    return (
        <div className="min-w-0">
            <ExamAddHeader subtitle={headerSubtitle} examId={createdExamId} questionCount={createdExamId ? questionCount : undefined} />
            <ExamProgressStepper
                current={step}
                questionCount={questionCount}
                canSelect={stageSelectable}
                onSelect={(first) => { setSubmitError(null); setStep(first) }}
            />

            <div className="min-w-0">
                {step === 1 && (
                    <form id="exam-details-form" onSubmit={createExam} noValidate className="grid grid-cols-1 gap-[18px] xl:grid-cols-12">
                        <PanelCard className="xl:col-span-7" title={`${kindTitle} details`} subtitle={detailsSubtitle}>
                            {created && <p className="mb-4 rounded-[12px] bg-ground px-3.5 py-2.5 text-[13px] text-muted-2">The details are saved. Change them from the exam page.</p>}
                            <fieldset disabled={created} className="m-0 min-w-0 border-0 p-0">
                                {isHubAdmin && (
                                    <div className="mb-5">
                                        <p className={WIZ_LABEL}>Students</p>
                                        <Segmented<'school' | 'uni'>
                                            label="Students"
                                            dense
                                            options={[{ key: 'school', label: 'School' }, { key: 'uni', label: 'University & college' }]}
                                            value={level}
                                            onChange={(v) => { setLevel(v); setSubmitError(null); setShowStep1Errors(false); if (v === 'uni') { setKind('exam'); setIsNational(false) } }}
                                        />
                                    </div>
                                )}
                                {!uniMode && (
                                <div className="mb-5">
                                    <p id="wiz-kind-label" className={WIZ_LABEL}>What are you creating?</p>
                                    <Segmented<ExamKind>
                                        label="What are you creating?"
                                        dense
                                        options={[{ key: 'exam', label: 'Exam' }, { key: 'worksheet', label: 'Worksheet' }, { key: 'homework', label: 'Homework' }]}
                                        value={kind}
                                        onChange={setKind}
                                    />
                                </div>
                                )}
                                {uniMode ? (
                                    <UniCoursePicker value={uni} onChange={(v) => { setUni(v); setSubmitError(null) }} showErrors={showStep1Errors} />
                                ) : (<>
                                <p id="wiz-grade-label" className={WIZ_LABEL}>Grade</p>
                                {gradeStatus === 'success' ? (
                                    <div role="group" aria-labelledby="wiz-grade-label" className="grid grid-cols-[repeat(auto-fill,minmax(120px,1fr))] gap-2.5">
                                        {gradeTiles.map((g: any) => renderGradeTile(g))}
                                        {gradeTiles.length === 0 && <p className="col-span-full text-[13px] text-muted">You teach no subject yet. Ask your school admin to assign you a subject class.</p>}
                                    </div>
                                ) : (
                                    <div className="flex items-center gap-2 text-sm text-muted"><Spinner size="sm" /> Loading grades…</div>
                                )}
                                {showStep1Errors && gradeError && <p className="mt-1.5 text-xs text-danger">{gradeError}</p>}

                                <p id="wiz-subject-label" className={`${WIZ_LABEL} mt-5`}>Subject</p>
                                {gradeStateId === '-1' ? (
                                    <p className="text-[13px] text-muted">Choose a grade to see its subjects.</p>
                                ) : subjectStatus === 'success' ? (
                                    <div role="group" aria-labelledby="wiz-subject-label" className="grid grid-cols-[repeat(auto-fill,minmax(120px,1fr))] gap-2.5">
                                        {subjectTiles.map((s: any) => renderSubjectTile(s))}
                                    </div>
                                ) : (
                                    <div className="flex items-center gap-2 text-sm text-muted"><Spinner size="sm" /> Loading subjects…</div>
                                )}
                                {showStep1Errors && subjectError && !gradeError && <p className="mt-1.5 text-xs text-danger">{subjectError}</p>}
                                </>)}

                                <hr className="my-5 h-px border-0 bg-hairline" />

                                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                                    <div className="md:col-span-2">
                                        <WizField label={`${kindTitle} name`} htmlFor="exam-name" required error={showStep1Errors ? nameError : undefined}>
                                            <input id="exam-name" type="text" placeholder="e.g. Midterm assessment" name="name" maxLength={NAME_MAX} value={formdata.name} onChange={handleInputChange} className={fieldClass} />
                                        </WizField>
                                    </div>
                                    {isExam && (
                                        <WizField label="Time (minutes)" htmlFor="exam-time" required hint="Students see a countdown; the exam auto-submits at zero." error={showStep1Errors ? timeError : undefined}>
                                            <input id="exam-time" type="number" min={1} max={300} placeholder="60" name="given_time" value={formdata.given_time} onChange={handleInputChange} className={fieldClass} />
                                        </WizField>
                                    )}
                                    {/* National exams are superuser-only (the backend refuses them otherwise), as in MixedExam. */}
                                    {role === 'superuser' && !uniMode && (
                                        <div className={isExam ? 'min-w-0' : 'min-w-0 md:col-span-2'}>
                                            <span className={WIZ_LABEL}>Exam type</span>
                                            <div className="flex min-h-[44px] items-center justify-between gap-4 rounded-[14px] border border-line bg-surface-2 px-3.5 py-2">
                                                <span className="min-w-0">
                                                    <span id="wiz-national-label" className="block font-semibold text-ink">Mark as national exam</span>
                                                    <span className="block text-xs text-muted">Links it to an EUEE paper</span>
                                                </span>
                                                <Switch tone="navy" checked={isNational} label="Mark as national exam" labelledBy="wiz-national-label"
                                                    onChange={(next) => { setIsNational(next); if (next) setSelectedNationalExam('-1') }} />
                                            </div>
                                        </div>
                                    )}
                                    <div className="md:col-span-2">
                                        <WizField label="Description" htmlFor="exam-description" optional>
                                            <textarea id="exam-description" rows={4} placeholder="Add an optional description" name="description" value={formdata.description} onChange={handleInputChange} className={`${fieldClass} min-h-[96px] resize-y py-2.5 leading-normal`} />
                                        </WizField>
                                    </div>
                                </div>
                                {role === 'superuser' && isNational && (
                                    <div className="mt-4">
                                        <WizField label="Choose national exam" htmlFor="exam-national-select">
                                            <select id="exam-national-select" aria-label="National exam select" disabled={nationalStatus !== 'success'} value={selectedNationalExam} onChange={(e) => setSelectedNationalExam(e.target.value)} className={fieldClass}>
                                                <option value="-1">Select a national exam</option>
                                                {nationalStatus === 'success' && (nationalData || []).map((n: any) => (
                                                    <option key={n.id} value={String(n.id)}>{n.name}</option>
                                                ))}
                                            </select>
                                        </WizField>
                                    </div>
                                )}
                            </fieldset>
                        </PanelCard>

                        {uniMode ? (
                            <PanelCard className="xl:col-span-5" title="Who sees it" subtitle="University and college exams go to a whole department.">
                                <p className="text-sm text-ink">
                                    {uni.fieldId == null
                                        ? 'Choose a department. Every student in it sees the exam once you publish it.'
                                        : `Every ${uni.fieldName} student, at any university or college, sees it once you publish it.`}
                                </p>
                                <p className="mt-3 rounded-[12px] bg-ground px-3.5 py-2.5 text-[13px] text-muted-2">Students see it under {uniOwnerName(uni)}.</p>
                            </PanelCard>
                        ) : (
                        <PanelCard className="xl:col-span-5" title="Assignment" subtitle="Who takes it and when it is due.">
                            <fieldset disabled={created} className="m-0 min-w-0 border-0 p-0">
                                <AssignFields gradeId={Number(gradeStateId) > 0 ? Number(gradeStateId) : null} gradeLabel={gradeLabel || undefined} subjectId={Number(subjectStateId) > 0 ? Number(subjectStateId) : null} kind={kind} value={assign} onChange={setAssign} errors={serverErrors} />
                            </fieldset>
                        </PanelCard>
                        )}
                    </form>
                )}

                {step === 2 && createdExamId && (
                    <div className="min-w-0">
                        {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
                        {/* @ts-ignore */}
                        <ExamQuestionAddCard
                            examId={createdExamId}
                            onProceedToMeta={memoizedOnProceedToMeta}
                            onRegisterExtractFn={memoizedOnRegisterExtractFn}
                            onRegisterProceedFn={memoizedOnRegisterProceedFn}
                            onQuestionsExtracted={memoizedOnQuestionsExtracted}
                            onQuestionsChange={setQuestionList}
                            initialQuestions={questionList}
                            isExtracting={isExtractingQuestions}
                            setIsExtracting={setIsExtractingQuestions}
                        />
                    </div>
                )}

                {step === 3 && createdExamId && (
                    pendingQuestions ? (
                        <div>
                            <ExamProcessingPipeline
                                examId={createdExamId}
                                inputQuestions={pendingQuestions}
                                // School mode: empty courseIds makes the catalog endpoint use the
                                // school_subject_id path. A normal exam is for one grade, so chapters
                                // and books come from that grade only; a national exam (EUEE) spans
                                // grades 9-12, so it passes no grade and gets them all.
                                // University mode: the chosen courses' chapters and books.
                                courseIds={uniMode ? uniCourseIds : NO_COURSES}
                                gradeId={!uniMode && !isNational && gradeStateId !== '-1' ? gradeStateId : undefined}
                                subjectId={!uniMode && subjectStateId !== '-1' ? subjectStateId : undefined}
                                sessionId={`add-exam-wizard-${createdExamId}`}
                                aiKnowledgeOnly={false}
                                schoolMode={!uniMode}
                                onSave={async (qs, info) => handleSaveQuestions(qs, info)}
                            />
                        </div>
                    ) : (
                        <div className="rounded-[20px] border border-line bg-warning-bg p-[22px] text-warning-ink">
                            <p className="font-display text-[17px] font-bold">No questions to review.</p>
                            <p className="mt-1 text-sm">Go back and extract or add questions first.</p>
                        </div>
                    )
                )}

                {step === 4 && createdExamId && review && (
                    <ExamReviewStep
                        examId={createdExamId}
                        questions={review}
                        timeMinutes={isExam && Number(formdata.given_time) > 0 ? Number(formdata.given_time) : null}
                        assign={assign}
                        gradeId={!uniMode && Number(gradeStateId) > 0 ? Number(gradeStateId) : null}
                        gradeLabel={gradeLabel}
                        partial={partial}
                    />
                )}
            </div>

            {submitError && (
                <p role="alert" className="mt-[18px] rounded-[12px] border border-danger/30 bg-danger-bg px-3.5 py-2.5 text-sm text-danger-ink">{submitError}</p>
            )}

            <WizardFooter note={footNote}>
                {step === 1 && (
                    <Button type="submit" form="exam-details-form" loading={isAdding}>
                        {isAdding ? 'Creating…' : isExam ? <>Next: add questions{NEXT_ICON}</> : `Create ${kindLabel}`}
                    </Button>
                )}

                {step === 2 && (
                    <>
                        <Button variant="ghost" icon={BACK_ICON} onClick={() => { setSubmitError(null); setStep(1) }}>Back</Button>
                        {!(questionsExtracted || questionList.length > 0) ? (
                            <Button onClick={() => { if (extractQuestionsFn) { extractQuestionsFn(); } }} disabled={isExtractingQuestions} loading={isExtractingQuestions}>
                                {isExtractingQuestions ? 'Extracting…' : 'Extract questions'}
                            </Button>
                        ) : (
                            <Button onClick={() => {
                                // Nothing left to process (every question deleted): do not send stale ones.
                                if (questionList.length === 0) setPendingQuestions(null)
                                else if (proceedToMetaFn) {
                                    proceedToMetaFn();
                                }
                                setStep(3);
                            }}>
                                Next: process questions{NEXT_ICON}
                            </Button>
                        )}
                    </>
                )}

                {step === 3 && (
                    <Button variant="ghost" icon={BACK_ICON} onClick={() => setStep(2)}>Back</Button>
                )}

                {step === 4 && createdExamId && (
                    <>
                        <Button variant="ghost" icon={<Save size={18} strokeWidth={1.8} aria-hidden />} onClick={() => navigate(`/exams/exam-detail/${createdExamId}`)}>Save as draft</Button>
                        <PublishExamButton examId={createdExamId} onPublished={() => navigate(`/exams/exam-detail/${createdExamId}`)} />
                    </>
                )}
            </WizardFooter>
        </div>
    )
}

const ExamAddWizard: React.FC = () => (
    <ExamSessionProvider>
        <ExamAddWizardInner />
    </ExamSessionProvider>
)

export default ExamAddWizard
